/**
 * @module builder
 */

import type { PresentationOptionV2 } from 'paintscout'

/**
 * One entry in the customer-facing presentation nav. `key` doubles as the scroll/anchor target id,
 * which is why it matches the underlying page or section key.
 */
export interface PresentationNavItem {
  key: string
  label: string
  /** Which tree level this came from — pages navigate, sections scroll. */
  level: 'page' | 'section'
}

/**
 * What the v2 customer-facing nav lists (#21990).
 *
 * The rule the issue asks for: **each page is a tab**, and *if there is only a single page, section
 * titles are used instead*. That keeps a one-page presentation — which is what every new
 * presentation and every converted scrolling presentation starts as — navigating exactly as v2
 * always has, while multi-page presentations get v1-style page tabs.
 *
 * A multi-page presentation deliberately does NOT list its sections: mixing both levels in one nav
 * is what made the old single-level nav confusing in the first place.
 *
 * Anything flagged `active: false` or `advanced.useForNav: false` is left out at whichever level is
 * being listed.
 */
export function getPresentationNavItems(args: {
  presentation?: Pick<PresentationOptionV2, 'pages'>
  /**
   * Hides items whose only content is the estimate, for quotes with nothing on them yet — matches
   * the existing `isQuoteEmpty` behaviour in the nav components.
   */
  isQuoteEmpty?: boolean
}): PresentationNavItem[] {
  const { presentation, isQuoteEmpty } = args
  const pages = (presentation?.pages ?? []).filter(isNavigable)

  if (!pages.length) {
    return []
  }

  if (pages.length === 1) {
    const sections = pages[0].sections?.filter(isNavigable) ?? []

    return sections
      .filter((section) => !isQuoteEmpty || !isQuoteOnly(section.blocks))
      .map((section) => ({
        key: section.key,
        label: section.title || section.label || '',
        level: 'section' as const
      }))
  }

  return pages
    .filter((page) => !isQuoteEmpty || !isQuoteOnly((page.sections ?? []).flatMap((section) => section.blocks ?? [])))
    .map((page) => ({
      key: page.key,
      label: page.label || page.title || '',
      level: 'page' as const
    }))
}

/**
 * Whether the nav should show a page or section. `active` is an explicit opt-out (absent means
 * shown, matching filterActivePresentationPages), `useForNav` likewise.
 */
function isNavigable(item: { active?: boolean; advanced?: { useForNav?: boolean } }): boolean {
  if (item?.active === false) {
    return false
  }
  return item?.advanced?.useForNav !== false
}

/** True when there's content and all of it is the estimate itself. */
function isQuoteOnly(blocks?: Array<{ type?: string }>): boolean {
  if (!blocks?.length) {
    return false
  }
  return blocks.every((block) => block.type === 'quote')
}
